import { useState, useMemo } from 'react';
import { 
  Card, 
  Table, 
  Text, 
  Group, 
  Select, 
  ActionIcon, 
  Tooltip, 
  Avatar, 
  Badge, 
  Title,
  Stack,
  TextInput,
  MultiSelect,
  Button
} from '@mantine/core';
import { mockTeams, mockTeamMembers, Team, TeamMember, TeamRole } from '../../types/team';
import { MagnifyingGlassIcon, UserPlusIcon, TrashIcon, UserMinusIcon } from '@heroicons/react/24/outline';

interface TeamAssignmentsProps {
  onAddMember: (teamId: string, memberId: string, role: TeamRole) => void;
  onRemoveMember: (teamId: string, memberId: string) => void;
}

export function TeamAssignments({ onAddMember, onRemoveMember }: TeamAssignmentsProps) {
  const [selectedTeam, setSelectedTeam] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [membersToAdd, setMembersToAdd] = useState<string[]>([]);
  const [memberRole, setMemberRole] = useState<TeamRole>('Member');

  // Transform teams for Select component
  const teamOptions = mockTeams.map(team => ({
    value: team.id,
    label: team.name
  }));

  // Get current team
  const currentTeam = useMemo(() => {
    return mockTeams.find(team => team.id === selectedTeam);
  }, [selectedTeam]);

  // Get team members
  const teamMembers = useMemo(() => {
    if (!currentTeam) return [];

    return mockTeamMembers.filter(member => 
      currentTeam.members.includes(member.id)
    );
  }, [currentTeam]);

  // Filter team members based on search query
  const filteredTeamMembers = useMemo(() => {
    if (searchQuery === '') return teamMembers;

    return teamMembers.filter(member => 
      member.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.role.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.email.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [teamMembers, searchQuery]);

  // Get available members (not in the team)
  const availableMembers = useMemo(() => {
    if (!currentTeam) return [];

    return mockTeamMembers
      .filter(member => !currentTeam.members.includes(member.id))
      .map(member => ({
        value: member.id,
        label: member.name,
        description: member.role
      }));
  }, [currentTeam]);

  // Handle adding members to the team
  const handleAddMembers = () => {
    if (!currentTeam || membersToAdd.length === 0) return;

    membersToAdd.forEach(memberId => {
      onAddMember(currentTeam.id, memberId, memberRole);
    });

    // Reset selection
    setMembersToAdd([]);
  };

  // Determine if a member is the team leader
  const isTeamLeader = (memberId: string): boolean => {
    return currentTeam?.leaderId === memberId;
  };

  // Render member role badge
  const renderRoleBadge = (memberId: string) => {
    if (isTeamLeader(memberId)) {
      return <Badge color="brand">Leader</Badge>;
    }
    return <Badge color="gray">Member</Badge>;
  };

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Title order={2} size="h3" mb="md">Team Assignments</Title>

      <Stack gap="md">
        <Select
          label="Select a Team"
          placeholder="Choose a team to manage members"
          data={teamOptions}
          value={selectedTeam}
          onChange={(value) => {
            if (value) setSelectedTeam(value);
            setSearchQuery('');
            setMembersToAdd([]);
          }}
          searchable
          clearable
        />

        {selectedTeam && (
          <>
            <Group grow align="flex-end">
              <MultiSelect
                label="Add Team Members"
                placeholder="Select members to add"
                data={availableMembers}
                searchable
                value={membersToAdd}
                onChange={setMembersToAdd}
                disabled={!selectedTeam}
                clearable
              />
              <Select
                label="Role"
                placeholder="Select role"
                data={[
                  { value: 'Member', label: 'Member' },
                  { value: 'Observer', label: 'Observer' }
                ]}
                value={memberRole}
                onChange={(value) => {
                  if (value as TeamRole) setMemberRole(value as TeamRole);
                }}
                disabled={!selectedTeam || membersToAdd.length === 0}
              />
              <Button 
                leftSection={<UserPlusIcon width={16} />}
                onClick={handleAddMembers}
                disabled={!selectedTeam || membersToAdd.length === 0}
                style={{ alignSelf: 'flex-end' }}
              >
                Add to Team
              </Button>
            </Group>

            <TextInput
              placeholder="Search team members..."
              leftSection={<MagnifyingGlassIcon width={16} />}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.currentTarget.value)}
              mb="md"
            />

            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Member</Table.Th>
                  <Table.Th>Role</Table.Th>
                  <Table.Th>Department</Table.Th>
                  <Table.Th>Skills</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredTeamMembers.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={5}>
                      <Text ta="center" c="dimmed" py="md">
                        {selectedTeam 
                          ? 'No team members match your search criteria.' 
                          : 'Select a team to view and manage its members.'}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  filteredTeamMembers.map((member) => (
                    <Table.Tr key={member.id}>
                      <Table.Td>
                        <Group gap="sm">
                          <Avatar 
                            color="gray" 
                            radius="xl"
                            src={member.avatar}
                          >
                            {member.name.substring(0, 2)}
                          </Avatar>
                          <div>
                            <Text size="sm" fw={500}>{member.name}</Text>
                            <Text size="xs" c="dimmed">{member.email}</Text>
                          </div>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="sm">
                          {renderRoleBadge(member.id)}
                          <Text size="sm">{member.role}</Text>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Badge variant="light">{member.department}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          {member.skills.map((skill, index) => (
                            <Badge key={index} size="sm" variant="outline">{skill}</Badge>
                          )).slice(0, 3)}
                          {member.skills.length > 3 && (
                            <Badge size="sm" variant="outline">+{member.skills.length - 3}</Badge>
                          )}
                        </Group>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        <Tooltip 
                          label={isTeamLeader(member.id) 
                            ? "Team leader cannot be removed" 
                            : "Remove from team"
                          }
                        >
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() => onRemoveMember(selectedTeam, member.id)}
                            disabled={isTeamLeader(member.id)}
                          >
                            <UserMinusIcon width={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}
              </Table.Tbody>
            </Table>
          </>
        )}
      </Stack>
    </Card>
  );
} 
